import { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api from '../../utils/api';
import './Business.css';

const businessTypes = ['Seller', 'Trader', 'Manufacturer', 'Manufacturer/Trader', 'Trader/Seller', 'Service Provider'];
const paymentModes = { Cash: ['COD'], UPI: ['Google Pay', 'BHIM UPI', 'Phone Pe', 'Paytm'], Online: ['EMI', 'NET Banking', 'Debit Card', 'Credit Card'] };
const DAYS = ['Monday','Tuesday','Wednesday','Thursday','Friday','Saturday','Sunday'];

export default function AddBusiness() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isLoggedIn, addBusiness } = useAuth();
  const logoInputRef = useRef(null);
  const imagesInputRef = useRef(null);
  const brochureInputRef = useRef(null);
  const [logoFile, setLogoFile] = useState(null);
  const [imageFiles, setImageFiles] = useState([]);
  const [brochureFile, setBrochureFile] = useState(null);

  const wantsAutofill = new URLSearchParams(location.search).get('autofill') === 'true';
  const [step, setStep] = useState(0);
  const [showAutofillPopup, setShowAutofillPopup] = useState(wantsAutofill);
  const [showCatModal, setShowCatModal] = useState(false);
  const [catSearch, setCatSearch] = useState('');
  const [activeCat, setActiveCat] = useState(null);
  const [locationLoading, setLocationLoading] = useState(false);

  const [form, setForm] = useState({
    businessName: '', businessType: '', personName: '', email: '',
    phone: '', whatsapp: '', pincode: '', state: '', district: '',
    city: '', houseNo: '', landmark: '', fullAddress: '',
    established: '', tagline: '', description: '', website: '', facebook: '', instagram: '', twitter: '', linkedin: '',
    deliveryRange: '', logistryEnabled: false,
  });
  const [logoPreview, setLogoPreview] = useState(null);
  const [images, setImages] = useState([]);
  const [brochure, setBrochure] = useState(null);
  const [categories, setCategories] = useState([]);
  const [selectedSubCats, setSelectedSubCats] = useState({});
  const [selectedPayments, setSelectedPayments] = useState(['COD']);
  const [hours, setHours] = useState(
    DAYS.reduce((a, d) => ({ ...a, [d]: { open: '09:00', close: '18:00', closed: false } }), {})
  );
  const [services, setServices] = useState([{ name: '', price: '' }]);
  const [products, setProducts] = useState([{ name: '', price: '', discount: '', description: '', image: null, imageFile: null }]);
  const [allCategories, setAllCategories] = useState([]);

  useEffect(() => {
    api.get('/api/categories/all').then(res => {
      const data = res.data.data;
      setAllCategories([...data.business.categories, ...data.professional.categories]);
    });
  }, []);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const toggleCat = (name) => {
    setCategories(prev => prev.includes(name) ? prev.filter(c => c !== name) : [...prev, name]);
    if (!categories.includes(name)) setActiveCat(name);
    else { setActiveCat(null); setSelectedSubCats(s => { const n = { ...s }; delete n[name]; return n; }); }
  };
  const toggleSubCat = (cat, sub) => setSelectedSubCats(prev => {
    const ex = prev[cat] || [];
    return { ...prev, [cat]: ex.includes(sub) ? ex.filter(s => s !== sub) : [...ex, sub] };
  });
  const togglePayment = (m) => setSelectedPayments(p => p.includes(m) ? p.filter(x => x !== m) : [...p, m]);

  const fetchLocation = async (lat, lon) => {
    const res = await fetch(`https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lon}&format=json`);
    const data = await res.json();
    const a = data.address || {};
    setForm(f => ({
      ...f,
      pincode: a.postcode || '',
      state: a.state || '',
      district: a.county || a.state_district || a.city || a.town || '',
      city: a.city || a.town || a.village || a.suburb || '',
      houseNo: (a.house_number ? a.house_number + ', ' : '') + (a.road || ''),
      landmark: a.suburb || a.neighbourhood || '',
      fullAddress: [a.road, a.suburb, a.city || a.town, a.county, a.state, a.postcode].filter(Boolean).join(', '),
    }));
  };

  const handleAutofill = () => {
    if (!navigator.geolocation) { setShowAutofillPopup(false); return; }
    setLocationLoading(true);
    navigator.geolocation.getCurrentPosition(async pos => {
      try { await fetchLocation(pos.coords.latitude, pos.coords.longitude); } catch {}
      setLocationLoading(false); setShowAutofillPopup(false);
    }, () => { setLocationLoading(false); setShowAutofillPopup(false); });
  };

  const handleUseLocation = () => {
    if (!navigator.geolocation) return;
    setLocationLoading(true);
    navigator.geolocation.getCurrentPosition(async pos => {
      try { await fetchLocation(pos.coords.latitude, pos.coords.longitude); } catch {}
      setLocationLoading(false);
    }, () => setLocationLoading(false));
  };

  const [showSuccess, setShowSuccess] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleFinalSubmit = async () => {
    setSubmitting(true);
    try {
      const fd = new FormData();
      // Basic form fields
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      // Categories & subcategories
      fd.append('categories', JSON.stringify(categories));
      fd.append('subCategories', JSON.stringify(selectedSubCats));
      fd.append('paymentModes', JSON.stringify(selectedPayments));
      fd.append('hours', JSON.stringify(hours));
      fd.append('services', JSON.stringify(services));
      fd.append('products', JSON.stringify(products.map(p => ({ name: p.name, price: p.price, discount: p.discount, description: p.description }))));
      // Files
      if (logoFile) fd.append('logo', logoFile);
      imageFiles.forEach(f => fd.append('images', f));
      if (brochureFile) fd.append('brochure', brochureFile);
      products.forEach((p, i) => { if (p.imageFile) fd.append('productImages', p.imageFile); });

      await addBusiness(fd);
      setShowSuccess(true);
      setTimeout(() => { setShowSuccess(false); navigate('/'); }, 3500);
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to submit business. Try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isLoggedIn) return (
    <div className="biz-login-wall">
      <div className="biz-login-card">
        <div className="biz-login-icon">🏢</div>
        <h2>Login Required</h2>
        <p>Please login to list your business.</p>
        <button className="biz-login-btn" onClick={() => navigate('/login')}>Sign Up / Login</button>
        <button className="biz-back-link" onClick={() => navigate(-1)}>← Go Back</button>
      </div>
    </div>
  );

  const steps = ['Business Info', 'Opening Hours', 'Upload Details', 'Our Services'];

  return (
    <div className="business-page">
      <div className="biz-header">
        <div className="container">
          <button className="back-btn-dark" onClick={() => step > 0 ? setStep(s => s - 1) : navigate(-1)}>← Back</button>
          <h1>Add Business</h1>
        </div>
      </div>

      <div className="container">
        {/* Step Indicator */}
        <div className="step-indicator">
          {steps.map((s, i) => (
            <div key={i} className={`step ${i === step ? 'active' : i < step ? 'done' : ''}`}>
              <div className="step-dot">{i < step ? '✓' : i + 1}</div>
              <span>{s}</span>
            </div>
          ))}
        </div>

        {/* ── STEP 0: Business Info ── */}
        {step === 0 && (
          <div className="biz-form">
            <div className="form-section-title">🧑 Basic Business Details</div>

            {/* Logo + Name row */}
            <div className="logo-name-row">
              <input ref={logoInputRef} type="file" accept="image/*" style={{ display: 'none' }}
                onChange={e => { const f = e.target.files[0]; if (f) { setLogoFile(f); setLogoPreview(URL.createObjectURL(f)); } }} />
              <div className="logo-upload" onClick={() => logoInputRef.current.click()}>
                {logoPreview ? <img src={logoPreview} alt="logo" /> : <><span>📷</span><p>Logo</p></>}
              </div>
              <div className="form-group" style={{ flex: 1 }}>
                <label>Business Name *</label>
                <input placeholder="Business Name" value={form.businessName} onChange={e => set('businessName', e.target.value)} required />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Business Type</label>
                <select value={form.businessType} onChange={e => set('businessType', e.target.value)}>
                  <option value="">Select Type</option>
                  {businessTypes.map(t => <option key={t}>{t}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>Established Year</label>
                <input type="number" placeholder="e.g. 2020" value={form.established} onChange={e => set('established', e.target.value)} />
              </div>
            </div>

            <div className="form-group">
              <label>Category</label>
              <button type="button" className="btn-add-cat" onClick={() => setShowCatModal(true)}>+ Add Category</button>
              {categories.length > 0 && (
                <div className="chips">
                  {categories.map(c => (
                    <span key={c} className="chip">
                      {c}{selectedSubCats[c]?.length > 0 && <em> › {selectedSubCats[c].join(', ')}</em>}
                      <button type="button" onClick={() => toggleCat(c)}>×</button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Contact Person</label>
                <input placeholder="Person Name" value={form.personName} onChange={e => set('personName', e.target.value)} />
              </div>
              <div className="form-group">
                <label>Business Phone *</label>
                <input type="tel" placeholder="Mobile Number" maxLength={10} value={form.phone}
                  onChange={e => set('phone', e.target.value.replace(/\D/g, '').slice(0, 10))} required />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Business Email *</label>
                <div className="input-with-btn">
                  <input type="email" placeholder="Email" value={form.email} onChange={e => set('email', e.target.value)} required />
                  <button type="button" className="btn-verify">Verify</button>
                </div>
              </div>
              <div className="form-group">
                <label>WhatsApp</label>
                <input type="tel" placeholder="WhatsApp Number" maxLength={10} value={form.whatsapp}
                  onChange={e => set('whatsapp', e.target.value.replace(/\D/g, '').slice(0, 10))} />
              </div>
            </div>

            <div className="form-group">
              <label>Tag Line</label>
              <input placeholder="e.g. Quality you can trust" value={form.tagline} onChange={e => set('tagline', e.target.value)} />
            </div>

            {/* Location */}
            <div className="form-section-title" style={{ marginTop: 8 }}>📍 Location Details</div>
            <p className="location-hint">ℹ️ You can change location as per your preference.</p>

            <div className="form-row">
              <div className="form-group">
                <label>Pincode</label>
                <div className="input-with-btn">
                  <input placeholder="Pincode" maxLength={6} value={form.pincode}
                    onChange={e => set('pincode', e.target.value.replace(/\D/g, '').slice(0, 6))} />
                  <button type="button" className="btn-verify" onClick={handleUseLocation} disabled={locationLoading}>
                    {locationLoading ? '...' : '📍'}
                  </button>
                </div>
              </div>
              <div className="form-group">
                <label>State</label>
                <input placeholder="State" value={form.state} onChange={e => set('state', e.target.value)} />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>District</label>
                <input placeholder="District" value={form.district} onChange={e => set('district', e.target.value)} />
              </div>
              <div className="form-group">
                <label>City / Town</label>
                <input placeholder="City or Town" value={form.city} onChange={e => set('city', e.target.value)} />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>House No. / Road</label>
                <input placeholder="House No., Road" value={form.houseNo} onChange={e => set('houseNo', e.target.value)} />
              </div>
              <div className="form-group">
                <label>Landmark / Area</label>
                <input placeholder="Landmark" value={form.landmark} onChange={e => set('landmark', e.target.value)} />
              </div>
            </div>

            <div className="form-group">
              <label>Full Address</label>
              <input placeholder="Full Address" value={form.fullAddress} onChange={e => set('fullAddress', e.target.value)} />
            </div>

            {/* Logistry */}
            <div className="form-group">
              <div className="logistry-row">
                <label>Business Logistry (Delivery)</label>
                <label className="toggle-switch">
                  <input type="checkbox" checked={form.logistryEnabled} onChange={e => set('logistryEnabled', e.target.checked)} />
                  <span className="toggle-slider" />
                </label>
              </div>
              {form.logistryEnabled && (
                <input type="number" placeholder="Delivery Range (KM)" value={form.deliveryRange}
                  onChange={e => set('deliveryRange', e.target.value)} style={{ marginTop: 10 }} />
              )}
            </div>

            {/* Description */}
            <div className="form-group">
              <label>Description</label>
              <textarea rows={3} placeholder="Write something about your business..." value={form.description}
                onChange={e => set('description', e.target.value)} />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Website (optional)</label>
                <input type="url" placeholder="https://..." value={form.website} onChange={e => set('website', e.target.value)} />
              </div>
              <div className="form-group">
                <label>Facebook (optional)</label>
                <input type="url" placeholder="https://facebook.com/..." value={form.facebook} onChange={e => set('facebook', e.target.value)} />
              </div>
              <div className="form-group">
                <label>Instagram (optional)</label>
                <input type="url" placeholder="https://instagram.com/..." value={form.instagram} onChange={e => set('instagram', e.target.value)} />
              </div>
              <div className="form-group">
                <label>Twitter (optional)</label>
                <input type="url" placeholder="https://twitter.com/..." value={form.twitter} onChange={e => set('twitter', e.target.value)} />
              </div>
              <div className="form-group">
                <label>LinkedIn (optional)</label>
                <input type="url" placeholder="https://linkedin.com/..." value={form.linkedin} onChange={e => set('linkedin', e.target.value)} />
              </div>
            </div>

            {/* Payment */}
            <div className="form-group">
              <label>Payment Modes</label>
              <div className="payment-grid">
                {Object.entries(paymentModes).map(([cat, modes]) => (
                  <div key={cat} className="payment-group">
                    <div className="payment-cat-title">
                      <span>{cat === 'Cash' ? '💵' : cat === 'UPI' ? '📲' : '💳'}</span><strong>{cat}</strong>
                    </div>
                    {modes.map(m => (
                      <label key={m} className="payment-mode">
                        <input type="checkbox" checked={selectedPayments.includes(m)} onChange={() => togglePayment(m)} />{m}
                      </label>
                    ))}
                  </div>
                ))}
              </div>
            </div>

            <button className="btn-next" onClick={() => {
              if (!form.businessName || !form.phone || !form.email) {
                alert('Business Name, Phone and Email are required.');
                return;
              }
              setStep(1);
            }}>Next: Opening Hours →</button>
          </div>
        )}

        {/* ── STEP 1: Opening Hours ── */}
        {step === 1 && (
          <div className="biz-form">
            <div className="form-section-title">🕒 Opening Hours</div>
            {DAYS.map(day => (
              <div key={day} className="hours-row">
                <span className="hours-day">{day}</span>
                <label className="toggle-switch" style={{ marginRight: 12 }}>
                  <input type="checkbox" checked={!hours[day].closed}
                    onChange={e => setHours(h => ({ ...h, [day]: { ...h[day], closed: !e.target.checked } }))} />
                  <span className="toggle-slider" />
                </label>
                {!hours[day].closed ? (
                  <div className="hours-times">
                    <input type="time" value={hours[day].open}
                      onChange={e => setHours(h => ({ ...h, [day]: { ...h[day], open: e.target.value } }))} />
                    <span>to</span>
                    <input type="time" value={hours[day].close}
                      onChange={e => setHours(h => ({ ...h, [day]: { ...h[day], close: e.target.value } }))} />
                  </div>
                ) : <span className="hours-closed">Closed</span>}
              </div>
            ))}
            <button className="btn-next" onClick={() => setStep(2)}>Next: Upload Details →</button>
          </div>
        )}

        {/* ── STEP 2: Upload Details ── */}
        {step === 2 && (
          <div className="biz-form">
            <div className="form-section-title">📷 Upload Details</div>

            <div className="form-group">
              <label>Business Images (Min 1, Max 5)</label>
              <input ref={imagesInputRef} type="file" accept="image/*" multiple style={{ display: 'none' }}
                onChange={e => {
                  const files = Array.from(e.target.files).slice(0, 5 - images.length);
                  setImageFiles(prev => [...prev, ...files].slice(0, 5));
                  setImages(prev => [...prev, ...files.map(f => URL.createObjectURL(f))].slice(0, 5));
                }} />
              {images.length < 5 && (
                <button type="button" className="btn-add-cat" onClick={() => imagesInputRef.current.click()}>+ Add Images ({images.length}/5)</button>
              )}
              {images.length > 0 && (
                <div className="upload-preview-grid">
                  {images.map((src, i) => (
                    <div key={i} style={{ position: 'relative' }}>
                      <img src={src} alt={`img-${i}`} />
                      <button type="button" onClick={() => setImages(imgs => imgs.filter((_, j) => j !== i))}
                        style={{ position: 'absolute', top: 4, right: 4, background: '#ef4444', color: '#fff', border: 'none', borderRadius: '50%', width: 22, height: 22, cursor: 'pointer', fontSize: 14, lineHeight: 1 }}>×</button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="form-group">
              <label>PDF Brochure (optional)</label>
              <input ref={brochureInputRef} type="file" accept=".pdf" style={{ display: 'none' }}
                onChange={e => { const f = e.target.files[0]; if (f) { setBrochureFile(f); setBrochure(f.name); } }} />
              <button type="button" className="btn-add-cat" onClick={() => brochureInputRef.current.click()}>+ Add Brochure</button>
              {brochure && <p style={{ fontSize: 13, color: '#16a34a', marginTop: 6 }}>📄 {brochure}</p>}
            </div>

            <button className="btn-next" onClick={() => {
              if (images.length < 1) { alert('Please add at least 1 business image.'); return; }
              setStep(3);
            }}>Next: Our Services →</button>
          </div>
        )}

        {/* ── STEP 3: Our Services ── */}
        {step === 3 && (
          <div className="biz-form">
            <div className="form-section-title">⚙️ Our Services</div>
            {services.map((s, i) => (
              <div key={i} className="product-form-card">
                <div className="product-form-header">
                  <span>Service {i + 1}</span>
                  {services.length > 1 && <button type="button" className="service-remove" onClick={() => setServices(sv => sv.filter((_, j) => j !== i))}>×</button>}
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Service Name</label>
                    <input placeholder="Service Name" value={s.name || ''}
                      onChange={e => setServices(sv => sv.map((x, j) => j === i ? { ...x, name: e.target.value } : x))} />
                  </div>
                  <div className="form-group">
                    <label>Price</label>
                    <input placeholder="e.g. ₹500" value={s.price || ''}
                      onChange={e => setServices(sv => sv.map((x, j) => j === i ? { ...x, price: e.target.value } : x))} />
                  </div>
                </div>
              </div>
            ))}
            <button type="button" className="btn-add-cat" onClick={() => setServices(s => [...s, { name: '', price: '' }])}>+ Add Service</button>

            <div className="form-section-title" style={{ marginTop: 24 }}>🛒 Our Products</div>
            {products.map((p, i) => (
              <div key={i} className="product-form-card">
                <div className="product-form-header">
                  <span>Product {i + 1}</span>
                  {products.length > 1 && <button type="button" className="service-remove" onClick={() => setProducts(pr => pr.filter((_, j) => j !== i))}>×</button>}
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Product Name</label>
                    <input placeholder="Product Name" value={p.name}
                      onChange={e => setProducts(pr => pr.map((x, j) => j === i ? { ...x, name: e.target.value } : x))} />
                  </div>
                  <div className="form-group">
                    <label>Price</label>
                    <input placeholder="e.g. ₹999" value={p.price}
                      onChange={e => setProducts(pr => pr.map((x, j) => j === i ? { ...x, price: e.target.value } : x))} />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Discount</label>
                    <input placeholder="e.g. 10%" value={p.discount}
                      onChange={e => setProducts(pr => pr.map((x, j) => j === i ? { ...x, discount: e.target.value } : x))} />
                  </div>
                  <div className="form-group">
                    <label>Product Image</label>
                    <input type="file" accept="image/*" style={{ padding: '8px 12px', borderRadius: 12 }}
                      onChange={e => {
                        const file = e.target.files[0];
                        if (file) setProducts(pr => pr.map((x, j) => j === i ? { ...x, image: URL.createObjectURL(file), imageFile: file } : x));
                      }} />
                  </div>
                </div>
                <div className="form-group">
                  <label>Description</label>
                  <textarea rows={2} placeholder="Product description..." value={p.description}
                    onChange={e => setProducts(pr => pr.map((x, j) => j === i ? { ...x, description: e.target.value } : x))} />
                </div>
                {p.image && <img src={p.image} alt="preview" style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 10, marginTop: 4 }} />}
              </div>
            ))}
            <button type="button" className="btn-add-cat" onClick={() => setProducts(p => [...p, { name: '', price: '', discount: '', description: '', image: null }])}>+ Add Product</button>

            <button className="btn-next" style={{ marginTop: 24, background: '#16a34a' }} onClick={handleFinalSubmit} disabled={submitting}>
              {submitting ? '⏳ Submitting...' : '✓ Submit Business'}
            </button>
          </div>
        )}
      </div>

      {/* Success Popup */}
      {showSuccess && (
        <div className="modal-overlay">
          <div className="success-popup">
            <div className="success-icon">🎉</div>
            <h2>Congratulations!</h2>
            <p>Your business has been listed successfully.</p>
            <p className="success-sub">Redirecting to home...</p>
            <div className="success-bar"><div className="success-bar-fill" /></div>
          </div>
        </div>
      )}

      {/* Autofill Popup */}
      {showAutofillPopup && (
        <div className="modal-overlay">
          <div className="autofill-popup">
            <div className="autofill-icon">❓</div>
            <h3>Autofill Address?</h3>
            <p className="autofill-warning">You Must Be At Your Business Location</p>
            <div className="autofill-actions">
              <button className="autofill-btn no" onClick={() => setShowAutofillPopup(false)}>No</button>
              <button className="autofill-btn yes" onClick={handleAutofill} disabled={locationLoading}>
                {locationLoading ? 'Fetching...' : 'Yes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Category Modal */}
      {showCatModal && (
        <div className="modal-overlay" onClick={() => setShowCatModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Select Categories</h3>
              <button onClick={() => setShowCatModal(false)}>✕</button>
            </div>
            <input className="modal-search" placeholder="Search..." value={catSearch}
              onChange={e => { setCatSearch(e.target.value); setActiveCat(null); }} />
            <div className="modal-list">
              {allCategories.filter(c => c.name.toLowerCase().includes(catSearch.toLowerCase())).map((cat, i) => (
                <div key={i}>
                  <label className="modal-item">
                    <input type="checkbox" checked={categories.includes(cat.name)} onChange={() => toggleCat(cat.name)} />
                    <span>{cat.icon}</span>
                    <span style={{ flex: 1 }}>{cat.name}</span>
                    {cat.subCategories?.length > 0 && (
                      <span className="modal-expand" onClick={e => { e.preventDefault(); setActiveCat(activeCat === cat.name ? null : cat.name); }}>
                        {activeCat === cat.name ? '▲' : '▼'}
                      </span>
                    )}
                  </label>
                  {activeCat === cat.name && cat.subCategories?.length > 0 && (
                    <div className="modal-subcats">
                      {cat.subCategories.map((sub, j) => (
                        <label key={j} className="modal-subcat-item">
                          <input type="checkbox" checked={(selectedSubCats[cat.name] || []).includes(sub.name)}
                            onChange={() => toggleSubCat(cat.name, sub.name)} />
                          <span>{sub.icon}</span><span>{sub.name}</span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
            <button className="btn-done" onClick={() => setShowCatModal(false)}>Done</button>
          </div>
        </div>
      )}
    </div>
  );
}
